import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import loadable from '@loadable/component';
import ms from 'ms';
import React from 'react';
import styled, { useTheme } from 'styled-components';

import ItemCardAuctionName from '@/components/GridItemCard/Card/components/ItemCardAuctionName/ItemCardAuctionName';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardMenu from '@/components/ItemCard/components/Menu/ItemCardMenu';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchLiveCatalogRegistrationByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { getLiveCatalogCurrentItemIndex } from '@/redux/modules/console';
import {
    getItemSummaryBidCount,
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummarySaleStartTs,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';

import { ItemCardComponent, ItemCardInfo } from '../../../types/types';
import ItemCardImage from '../../components/ItemCardImage/ItemCardImage';
import ItemCardPriceRow from '../../components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardShortDate from '../../components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '../../components/ItemCardTitle/ItemCardTitle';

/** Establish connection to real-time auction events, only on the client-side */
const LiveAuctionEventsSubscriber = loadable(
    () => import('@/components/LiveEventSubscribers/LiveAuctionEventsSubscriber'),
    {
        ssr: false,
    }
);

const FollowedSearchItemPageItemCard: ItemCardComponent = ({ className, itemId }) => {
    const dispatch = useAppDispatch();

    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const [followedSearchItemCardHovered, setFollowedSearchItemCardHovered] = React.useState(false);

    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));

    // Logic for when an auction is live
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;

    const previousCatalogStatus = usePrevious(catalogStatus);

    React.useEffect(() => {
        if (previousCatalogStatus !== 'live' && catalogStatus === 'live') {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
            dispatch(fetchCatalogPresence(catalogId));
            dispatch(
                fetchLiveCatalogRegistrationByIdIfNeeded({
                    catalogId,
                    sellerId,
                })
            );
        }
    }, [catalogId, catalogStatus, sellerId, dispatch, previousCatalogStatus]);

    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();
    const isMobile = useIsNarrowerThan(tabletNarrowWidthNum + 1);

    const catalogLiveWithinAWeek = catalogStatus !== 'done' && Date.now() > saleStartTs * 1000 - ms('7d');
    const shouldNotShowAuctionName =
        (catalogStatus === 'live' || catalogStatus === 'online') && isMobile && catalogLiveWithinAWeek;
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const biddingWithin7Days = Date.now() > saleStartTs * 1000 - ms('7d');
    const showDateWithoutYear = catalogStatus === 'online' && !biddingWithin7Days;
    const showDateWithOnlyYear = catalogStatus === 'done';
    const isBuyNowOnMobile = isMobile && Boolean(buyNowStatus);
    const showAuctionName = !shouldNotShowAuctionName || !isBuyNowOnMobile;
    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;

    return (
        <FollowedSearchPageItemCardContainer
            className={className}
            data-testid="my-bids-item-card"
        >
            {comingUp && (
                <LiveAuctionEventsSubscriber
                    catalogId={catalogId}
                    limitMessagesFilter="itemCard"
                />
            )}
            <StyledImageContainer
                data-testid="image-container"
                onMouseEnter={() => setFollowedSearchItemCardHovered(true)}
                onMouseLeave={() => setFollowedSearchItemCardHovered(false)}
            >
                <StyledFavoriteButton>
                    <ItemCardSaveIcon
                        canShowEasyShipping
                        itemId={itemId}
                    />
                </StyledFavoriteButton>
                <ItemCardImage
                    itemId={itemId}
                    showBidStatus
                    useInFluidGrid
                />
            </StyledImageContainer>

            <StyledPlacedAuctionNameAndDate>
                <StyledItemCardShortDate
                    allowBidding
                    itemId={itemId}
                    showDateWithoutYear={showDateWithoutYear}
                    showOnlyYear={showDateWithOnlyYear}
                />
                {showAuctionName && <ItemCardAuctionName itemId={itemId} />}
            </StyledPlacedAuctionNameAndDate>

            <ItemCardTitle
                isHovered={followedSearchItemCardHovered}
                itemId={itemId}
                onMouseEnter={() => setFollowedSearchItemCardHovered(true)}
                onMouseLeave={() => setFollowedSearchItemCardHovered(false)}
            />

            <PlacedPriceInformation
                onMouseEnter={() => {
                    setFollowedSearchItemCardHovered(true);
                }}
                onMouseLeave={() => {
                    setFollowedSearchItemCardHovered(false);
                }}
            >
                <StyledBidInfo>
                    <ItemCardPriceRow
                        isHovered={followedSearchItemCardHovered}
                        itemId={itemId}
                    />
                    {!isSold && !isPassed && !isPending && bidCount > 0 && (
                        <Caption
                            className={cn(
                                'ml-xs text-text-secondary',
                                followedSearchItemCardHovered && 'cursor-pointer text-link-hover underline'
                            )}
                            data-testid="bid-count"
                            onMouseOut={() => {
                                setFollowedSearchItemCardHovered(false);
                            }}
                            onMouseOver={() => {
                                setFollowedSearchItemCardHovered(true);
                            }}
                        >
                            <FormattedMessage
                                id="itemBidding.bid"
                                values={{ bidCount }}
                            />
                        </Caption>
                    )}
                </StyledBidInfo>
                {isLoggedIn && (
                    <ItemCardMenu
                        itemId={itemId}
                        showIcon={followedSearchItemCardHovered}
                    />
                )}
            </PlacedPriceInformation>
        </FollowedSearchPageItemCardContainer>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: FollowedSearchItemPageItemCard,
};

export default info;

const FollowedSearchPageItemCardContainer = styled.div`
    position: relative;
    min-width: 0;
`;

const StyledImageContainer = styled.section`
    position: relative;
    aspect-ratio: 1 / 1;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StyledItemCardShortDate = styled(ItemCardShortDate)`
    margin-right: ${({ theme }) => theme.spacing.xs};
    flex-grow: 1;
`;

const StyledPlacedAuctionNameAndDate = styled.span`
    display: flex;
    justify-content: space-between;
    white-space: nowrap;
    margin-bottom: ${({ theme }) => theme.spacing.xxs};
`;

const PlacedPriceInformation = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledFavoriteButton = styled.span`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
`;

const StyledBidInfo = styled.div`
    display: flex;
    align-items: center;
`;
